import { useState, useCallback } from 'react';
import { useDashboard, Annotation } from '../context/DashboardContext';

/**
 * Per-chart annotations. Text notes pinned to a specific x-axis value.
 * Persisted to localStorage via DashboardContext.
 */
export function useAnnotations(chartId: string) {
  const { annotations, addAnnotation, removeAnnotation } = useDashboard();
  const [drafting, setDrafting] = useState<{ xValue: string; yKey: string } | null>(null);
  const [draftText, setDraftText] = useState('');

  const chartAnnotations: Annotation[] = annotations[chartId] || [];

  const startDraft = useCallback((xValue: string, yKey: string) => {
    setDrafting({ xValue, yKey });
    setDraftText('');
  }, []);

  const commitDraft = useCallback(() => {
    if (!drafting || !draftText.trim()) { setDrafting(null); return; }
    addAnnotation(chartId, { xValue: drafting.xValue, yKey: drafting.yKey, text: draftText.trim() });
    setDrafting(null);
    setDraftText('');
  }, [drafting, draftText, chartId, addAnnotation]);

  const cancelDraft = useCallback(() => { setDrafting(null); setDraftText(''); }, []);

  const remove = useCallback((id: string) => removeAnnotation(chartId, id), [chartId, removeAnnotation]);

  return {
    annotations: chartAnnotations,
    drafting,
    draftText,
    setDraftText,
    startDraft,
    commitDraft,
    cancelDraft,
    remove,
  };
}
